import clsx from 'clsx';
import React from 'react';

import { Emblem, EmblemProps } from '../Emblem';
import { Icon, IconProps } from '../Icon';
import { BodyText } from '../Typography/BodyText';
import { HeaderText } from '../Typography/HeaderText';

enum EnhancementEnum {
  'Training provided',
  'Relocation assistance',
  'Urgent',
  'Veteran-friendly',
  'Signing bonus',
}
enum RemoteVariantEnum {
  'Remote',
  'Hybrid',
  'In-person',
}
enum SalarySourceEnum {
  'provided',
  'parsed',
  'predicted',
}
enum BenefitEnum {
  'Medical',
  'Dental',
  'Vision',
  '401k',
  'Life',
}
enum JobTypeEnum {
  'Full-time',
  'Part-time',
  'Contract',
  'Contract to hire',
  'Temporary',
  'Per diem',
  'Other',
}
enum QualificationGradeEnum {
  'great' = 'Great match',
  'good' = 'Good match',
  'fair' = 'Fair match',
  'poor' = 'Poor match',
}

export type Enhancement = keyof typeof EnhancementEnum;
export type RemoteVariant = keyof typeof RemoteVariantEnum;
export type SalarySource = keyof typeof SalarySourceEnum;
export type Benefit = keyof typeof BenefitEnum;
export type JobType = keyof typeof JobTypeEnum;
export type QualificationGrade = keyof typeof QualificationGradeEnum;

export function isEnhancement(str: string): str is Enhancement {
  return EnhancementEnum[str as keyof typeof EnhancementEnum] !== undefined;
}

export function isRemoteVariant(str: string): str is RemoteVariant {
  return RemoteVariantEnum[str as keyof typeof RemoteVariantEnum] !== undefined;
}

export function isSalarySource(str: string): str is SalarySource {
  return SalarySourceEnum[str as keyof typeof SalarySourceEnum] !== undefined;
}

export function isBenefit(str: string): str is Benefit {
  return BenefitEnum[str as keyof typeof BenefitEnum] !== undefined;
}

export function isJobType(str: string): str is JobType {
  return JobTypeEnum[str as keyof typeof JobTypeEnum] !== undefined;
}

export function isQualificationGrade(str: string): str is QualificationGrade {
  return QualificationGradeEnum[str as keyof typeof QualificationGradeEnum] !== undefined;
}

export type JobCardProps = {
  title: string;
  companyLogoUrl?: string;
  companyName: string;
  companyUrl?: string;
  location: string;
  locationUrl?: string;
  enhancements?: Array<Enhancement>;
  remoteVariant?: RemoteVariant;
  formattedSalary?: string;
  salarySource?: SalarySource;
  benefits?: string | Array<Benefit>;
  jobType?: JobType;
  snippet?: string;
  href?: string;
  qualificationGrade?: QualificationGrade;
  isNew?: boolean;
  renderTopRightLabel?: () => JSX.Element;
  renderActionBar?: () => JSX.Element;
};

const hyperlinkClasses = clsx(
  'relative z-20',
  'text-black hover:text-black active:text-black focus:text-black visited:text-black',
  'hover:underline active:underline focus:underline',
);

export const JobCard = (props: JobCardProps): JSX.Element => {
  const {
    title,
    companyLogoUrl,
    companyName,
    companyUrl,
    location,
    locationUrl,
    enhancements,
    remoteVariant,
    formattedSalary,
    salarySource,
    benefits,
    jobType,
    snippet,
    href,
    qualificationGrade,
    isNew = false,
    renderTopRightLabel,
    renderActionBar,
  } = props;

  const enhancementIconMap = new Map<Enhancement, IconProps['name']>([
    ['Relocation assistance', 'movingTruck'],
    ['Training provided', 'awardRibbon'],
    ['Urgent', 'clock'],
    ['Signing bonus', 'salaryBill'],
    ['Veteran-friendly', 'veteranFlag'],
  ]);

  const getEnhancementIcon = (enhancement: Enhancement): JSX.Element | null => {
    const enhancementIconName = enhancementIconMap.get(enhancement);
    return enhancementIconName ? <Icon name={enhancementIconName} size="small" /> : null;
  };

  const qualificationGradeEmblemMap = new Map<QualificationGrade, EmblemProps['name']>([
    ['poor', 'matchPoor'],
    ['fair', 'matchFair'],
    ['good', 'matchGood'],
    ['great', 'matchGreat'],
  ]);

  const getQualificationGradeEmblem = (qualificationGrade: QualificationGrade): JSX.Element | null => {
    const gradeEmblemName = qualificationGradeEmblemMap.get(qualificationGrade);
    return gradeEmblemName ? <Emblem name={gradeEmblemName} /> : null;
  };

  const newLabel = isNew && (
    <div className="ml-4 inline-block align-top leading-[12px]">
      <BodyText small inline bold>
        <span className="text-gray">NEW!</span>
      </BodyText>
    </div>
  );

  return (
    <article className="relative w-auto bg-white text-black">
      <div>
        <div className="mb-[19px] flex justify-between">
          <div>
            {companyLogoUrl ? (
              <img
                src={companyLogoUrl}
                srcSet={`${companyLogoUrl.replace('s30x30', 's140x140')} 1x`}
                alt={title}
                width="40"
                height="40"
                className="max-h-[40px] w-auto max-w-[80px]"
              />
            ) : (
              <Icon name="buildingSmall" size="xlarge" color="gray" />
            )}
          </div>
          <div className="">{renderTopRightLabel && renderTopRightLabel()}</div>
        </div>
        <HeaderText level={2} size="sm" aria-label={title}>
          {href ? (
            <a
              href={href}
              target="_self"
              className={clsx([
                'text-black visited:text-black hover:text-black focus:text-black active:text-black',
                'hover:no-underline focus:no-underline active:no-underline',
                'after:absolute after:top-0 after:right-0 after:bottom-0 after:left-0 after:z-10 after:content-[""]',
              ])}
            >
              {title}
              {newLabel}
            </a>
          ) : (
            <div>
              {title}
              {newLabel}
            </div>
          )}
        </HeaderText>
        <div className="mt-[5px] mb-[19px] flex flex-col gap-[3px]">
          <BodyText>
            <a href={companyUrl} className={hyperlinkClasses}>
              {companyName}
            </a>
          </BodyText>
          <BodyText>
            <a href={locationUrl} className={hyperlinkClasses}>
              {location}
            </a>
          </BodyText>
        </div>
        {qualificationGrade && (
          <div className="mb-[21px] flex gap-4">
            {getQualificationGradeEmblem(qualificationGrade)}
            <BodyText>{QualificationGradeEnum[qualificationGrade]}</BodyText>
          </div>
        )}
        {enhancements && enhancements.length > 0 && (
          <div className="mb-[14px]">
            <div className="flex flex-wrap gap-8">
              {enhancements?.map((enhancement) => (
                <div key={enhancement} className="flex flex-row gap-4 rounded-48 border py-8 px-12">
                  {getEnhancementIcon(enhancement)}
                  <BodyText small>{enhancement}</BodyText>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="mb-[18px] flex flex-col gap-[3px]">
          {remoteVariant && <BodyText>{remoteVariant}</BodyText>}
          {formattedSalary && (
            <div className="flex">
              <BodyText>{formattedSalary}</BodyText>
              {salarySource === 'predicted' && (
                <div className="ml-8 flex flex-row gap-4 rounded-4 bg-gray py-4 px-8 font-bold">
                  <BodyText small>Estimated pay</BodyText>
                  <Icon name="information" size="small" />
                </div>
              )}
            </div>
          )}
          {benefits && <BodyText>{Array.isArray(benefits) ? benefits?.join(', ') : benefits}</BodyText>}
          {jobType && <BodyText>{jobType}</BodyText>}
        </div>
        {snippet && (
          <div className="mb-[18px]">
            <BodyText small>
              <span dangerouslySetInnerHTML={{ __html: snippet }}></span>
            </BodyText>
          </div>
        )}
        <div className="relative z-20 mt-[3px]">{renderActionBar && renderActionBar()}</div>
      </div>
    </article>
  );
};
